import { roundTo } from '@23degrees/shared-code/utilities';
import { Directive, ElementRef, EventEmitter, Input, OnDestroy, Output, afterNextRender, inject } from '@angular/core';

const observerMap = new WeakMap<Element, ResizeObserverDirective>();

@Directive({
  selector: '[resizeObserver]',
  standalone: true,
})
export class ResizeObserverDirective implements OnDestroy {
  @Input() caller = 'unknown';
  @Input() useParent = false;

  @Input() forceEmit = false;
  // @Input() debounce = 0;

  @Output() resize = new EventEmitter<ResizeObserverEntry>();

  private resizeObs: ResizeObserver;
  private elementRef = inject<ElementRef<Element>>(ElementRef);

  constructor() {
    afterNextRender(() => {
      let target = this.elementRef.nativeElement;
      if (this.useParent) {
        target = target.parentElement;
      }

      this.resizeObs = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (observerMap.has(entry.target)) {
            const roD = observerMap.get(entry.target);
            roD.resizeCallback(entry);
          }
        }
      });

      observerMap.set(target, this);
      this.resizeObs.observe(target);
    });
  }

  ngOnDestroy() {
    let target = this.elementRef.nativeElement;
    if (this.useParent) {
      target = target.parentElement;
    }

    if (this.resizeObs) {
      this.resizeObs.unobserve(target);
      observerMap.delete(target);
    }
  }

  public resizeCallback = (entry: ResizeObserverEntry) => {
    const { width, height } = entry.contentRect;
    const currentRect = this.elementRef.nativeElement.getBoundingClientRect();

    requestAnimationFrame(() => {
      const nextRect = this.elementRef.nativeElement.getBoundingClientRect();
      const isSameWidth = roundTo(currentRect.width, 3) === roundTo(nextRect.width, 3);
      const isSameHeight = roundTo(currentRect.height, 3) === roundTo(nextRect.height, 3);

      if ((isSameWidth && isSameHeight) || this.forceEmit) {
        this.resize.emit(entry);
      } else {
        // we are still animating
      }
    });
  };
}
